import { useEffect, useRef, type ReactNode } from 'react';
import { cx } from '@whinerp/ui';

/**
 * A centred overlay: the shell's search and command palette both sit in one.
 *
 * Escape closes, focus moves in on open and back out on close, and the
 * background is inert to the keyboard while it is open. Written once because
 * every overlay that implements its own focus handling implements it slightly
 * differently, and the one that gets it wrong is the one nobody tested with a
 * keyboard.
 */
export function Overlay({
  open,
  onClose,
  label,
  children,
  className,
}: {
  open: boolean;
  onClose: () => void;
  /** Announced when the overlay opens. */
  label: string;
  children: ReactNode;
  className?: string;
}): JSX.Element | null {
  const panel = useRef<HTMLDivElement>(null);
  const restoreTo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    restoreTo.current = document.activeElement as HTMLElement | null;
    const focusable = panel.current?.querySelector<HTMLElement>('input, button, [tabindex]:not([tabindex="-1"])');
    focusable?.focus();

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      restoreTo.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center bg-canvas/80 p-6 pt-7"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className={cx('w-full max-w-modal rounded-card bg-surface shadow-modal', className)}
      >
        {children}
      </div>
    </div>
  );
}
